import { useEffect, useState } from "react";
import { Wand2, Loader2, ShieldCheck, MessageSquareText, SlidersVertical, GitCompare, Activity } from "lucide-react";
import MixBusPanel from "@/components/MixBusPanel";
import ABCompare from "@/components/ABCompare";
import SpectrumCurve from "@/components/SpectrumCurve";
import WordTimeline from "@/components/WordTimeline";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError, audioUrl } from "@/lib/api";

const GRADE = { A: "text-emerald-400 border-emerald-500/40", B: "text-sky-300 border-sky-500/40", C: "text-amber-300 border-amber-500/40", D: "text-red-300 border-red-500/40" };
let fxCache = null;

// Server-side voice FX (Voicemod/Krotos-style, ffmpeg) → new creation, plus TechValidator QA for voices/SFX.
export default function VoiceFxRow({ v, onSaved }) {
  const [effects, setEffects] = useState(fxCache || {});
  const [effect, setEffect] = useState("robot");
  const [busy, setBusy] = useState(false);
  const [qaBusy, setQaBusy] = useState(false);
  const [qa, setQa] = useState(v.qa || null);
  const [sync, setSync] = useState(v.lipsync || null);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncBusy, setSyncBusy] = useState(false);
  const [mixOpen, setMixOpen] = useState(false);
  const [abOpen, setAbOpen] = useState(false);
  const [specOpen, setSpecOpen] = useState(false);
  const toggleSync = async () => {
    if (syncOpen) return setSyncOpen(false);
    if (!sync) {
      setSyncBusy(true);
      try { const { data } = await api.get(`/voice/${v.id}/lipsync`); setSync(data); }
      catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Lipsync indisponible."); return; }
      finally { setSyncBusy(false); }
    }
    setSyncOpen(true);
  };
  useEffect(() => { if (fxCache) return; api.get("/voice/runtime/effects").then(({ data }) => { fxCache = data.effects; setEffects(data.effects); }).catch(() => {}); }, []);

  const apply = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/voice/${v.id}/fx`, { effect }); toast.success(`Nouvelle création #${data.seq} · ${effects[effect]?.label || effect} (gratuit).`); onSaved?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Effet impossible."); }
    finally { setBusy(false); }
  };
  const validate = async () => {
    setQaBusy(true);
    try { const { data } = await api.post(`/voice/${v.id}/validate`); setQa(data.qa); toast.success(`QA ${data.qa.grade} (${data.qa.score}/100)`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Validation impossible."); }
    finally { setQaBusy(false); }
  };
  return (
    <div className={`space-y-2 ${syncOpen || mixOpen || abOpen || specOpen ? "w-full basis-full" : ""}`}>
    <div className="flex items-center gap-1.5 flex-wrap" data-testid={`voice-fxrow-${v.seq}`}>
      {v.kind === "tts" && (
        <Button variant="outline" size="sm" onClick={toggleSync} disabled={syncBusy} className={`h-8 gap-1.5 ${syncOpen ? "border-primary text-primary" : "text-muted-foreground"}`} data-testid={`voice-lipsync-toggle-${v.seq}`}>{syncBusy ? <Loader2 size={13} className="animate-spin" /> : <MessageSquareText size={13} />} Lipsync</Button>
      )}
      <Select value={effect} onValueChange={setEffect}>
        <SelectTrigger className="h-8 w-36 text-xs bg-background border-border" data-testid={`voice-fx-select-${v.seq}`}><SelectValue /></SelectTrigger>
        <SelectContent className="glass border-border">{Object.entries(effects).filter(([k]) => k !== "none").map(([k, e]) => <SelectItem key={k} value={k}>{e.label}</SelectItem>)}</SelectContent>
      </Select>
      <Button variant="outline" size="sm" onClick={apply} disabled={busy} className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 gap-1.5 h-8" data-testid={`voice-fx-apply-${v.seq}`}>{busy ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />} Voix FX</Button>
      <Button variant="outline" size="sm" onClick={() => setMixOpen((o) => !o)} className={`h-8 gap-1.5 ${mixOpen ? "border-cyan-400 text-cyan-300 bg-cyan-500/10" : "border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10"}`} title="Channel strip, réverb, limiteur — gratuit" data-testid={`voice-mixbus-open-${v.seq}`}><SlidersVertical size={13} /> Mix Bus</Button>
      {v.mix_of && <Button variant="outline" size="sm" onClick={() => setAbOpen((o) => !o)} className={`h-8 gap-1.5 ${abOpen ? "border-cyan-400 text-cyan-300 bg-cyan-500/10" : "border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10"}`} title="Comparer original / Mix Bus" data-testid={`voice-ab-open-${v.seq}`}><GitCompare size={13} /> A/B</Button>}
      {qa?.grade ? (
        <button type="button" onClick={() => qa.spectrum && setSpecOpen((o) => !o)} className={`text-[10px] font-mono px-2 py-0.5 rounded-full border inline-flex items-center gap-1 ${GRADE[qa.grade] || ""}`} title={(qa.issues?.length ? qa.issues.join(" · ") : "Aucun défaut technique") + (qa.spectrum ? " · clic : spectre" : "")} data-testid={`voice-qa-badge-${v.seq}`}><ShieldCheck size={10} /> QA {qa.grade} · {qa.score}{qa.spectrum && <Activity size={10} className="opacity-70" />}</button>
      ) : (
        <button type="button" onClick={validate} disabled={qaBusy} className="text-[10px] font-mono px-2 py-0.5 rounded-full border border-border text-muted-foreground hover:text-foreground inline-flex items-center gap-1 h-6" data-testid={`voice-qa-run-${v.seq}`}>{qaBusy ? <Loader2 size={10} className="animate-spin" /> : <ShieldCheck size={10} />} QA</button>
      )}
    </div>
    {mixOpen && <MixBusPanel id={v.id} kind="voice" seq={`v${v.seq}`} onSaved={() => { setMixOpen(false); onSaved?.(); }} />}
    {abOpen && v.mix_of && <ABCompare gen={{ ...v, qa }} kind="voice" testId={`voice-ab-${v.seq}`} />}
    {specOpen && qa?.spectrum && !abOpen && <SpectrumCurve spectrum={qa.spectrum} testId={`voice-spectrum-${v.seq}`} />}
    {syncOpen && sync && <WordTimeline src={audioUrl(v.audio_path)} lipsync={sync} seconds={v.audio_duration || sync.seconds || 0} compact />}
    </div>
  );
}
